import { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import AdminShell from "../../components/AdminShell";
import { toast } from "sonner";
import {
  Loader2, Search, ExternalLink, Lock, Unlock, Image as ImageIcon,
  AlertTriangle, Calculator, FileText,
} from "lucide-react";

/**
 * The question bank, as a tutor sees it.
 *
 * These questions do not live in this site's database. They are real Edexcel
 * past-paper questions in a separate Postgres bank, reviewed one by one on the
 * office PC and pushed to the cloud; this page reads them through
 * /api/admin/bank/*, which the backend proxies using a token.
 *
 * Two things about what is shown here are deliberate:
 *
 *   - **The paper is named.** Students never see it - their side of the bank
 *     cannot even return it - but a tutor choosing what to set needs
 *     "1MA1/2H Nov 2022 Q20" to find it again and to avoid setting the same
 *     paper twice in a term.
 *   - **Preview opens the student page**, rather than rendering the question
 *     again here. A second rendering would drift from the first, and the
 *     useful question is "what will the child actually see?".
 */

const DIFFICULTIES = [
  { value: "bronze", label: "Bronze", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  { value: "silver", label: "Silver", cls: "bg-slate-100 text-slate-700 border-slate-300" },
  { value: "gold",   label: "Gold",   cls: "bg-yellow-50 text-yellow-800 border-yellow-300" },
];

const PAGE = 25;

function Band({ value }) {
  const d = DIFFICULTIES.find((x) => x.value === value);
  if (!d) return <span className="text-slate-400 text-xs">—</span>;
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold border ${d.cls}`}>
      {d.label}
    </span>
  );
}

export default function AdminBank() {
  const [status, setStatus] = useState(null);
  const [statusError, setStatusError] = useState(null);
  const [topics, setTopics] = useState([]);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [siteUrl, setSiteUrl] = useState("");

  const [search, setSearch] = useState("");
  const [code, setCode] = useState("");
  const [topic, setTopic] = useState("");
  const [difficulty, setDifficulty] = useState("");
  const [calculator, setCalculator] = useState("");
  // The widest cut of all: a Year 8 worksheet and a GCSE mock share no
  // questions, so this is the filter to reach for first.
  const [stage, setStage] = useState("");
  const [source, setSource] = useState("");

  useEffect(() => {
    api.get("/admin/bank/status")
      .then((r) => { setStatus(r.data); setSiteUrl(r.data.site_url || ""); })
      // Said out loud rather than left as an empty table: a bank that is
      // unreachable looks exactly like a bank with nothing in it.
      .catch((e) => setStatusError(e.response?.data?.detail || "The question bank is not responding."));
    api.get("/admin/bank/topics")
      .then((r) => setTopics(r.data || []))
      .catch(() => setTopics([]));
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = { limit: PAGE, offset };
    if (search.trim()) params.q = search.trim();
    if (code.trim()) params.code = code.trim();
    if (topic) params.topic = topic;
    if (difficulty) params.difficulty = difficulty;
    if (calculator) params.calculator = calculator;
    if (stage) params.stage = stage;
    if (source) params.source = source;

    api.get("/admin/bank/questions", { params })
      .then((r) => { setRows(r.data.questions || []); setTotal(r.data.total || 0); setSiteUrl(r.data.site_url || siteUrl); })
      .catch((e) => {
        setRows([]); setTotal(0);
        toast.error(e.response?.data?.detail || "Could not load questions");
      })
      .finally(() => setLoading(false));
  }, [search, code, topic, difficulty, calculator, stage, source, offset, siteUrl]);

  useEffect(() => { load(); }, [load]);

  const reset = (fn) => { fn(); setOffset(0); };

  return (
    <AdminShell title="Question Bank" subtitle={status ? `${status.questions.toLocaleString("en-GB")} reviewed questions` : "Edexcel past-paper questions"}>
      {statusError && (
        <div className="mb-5 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3">
          <AlertTriangle size={18} className="mt-0.5 shrink-0 text-red-600" />
          <div className="text-sm text-red-900">
            <div className="font-semibold">The question bank is not reachable.</div>
            <div className="mt-0.5 text-red-700">{statusError}</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="mb-5 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[220px] flex-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={(e) => reset(() => setSearch(e.target.value))}
              placeholder="Search the wording…"
              className="w-full rounded-lg border border-slate-300 py-2 pl-9 pr-3 text-sm"
            />
          </div>
          <input
            value={code}
            onChange={(e) => reset(() => setCode(e.target.value.toUpperCase()))}
            placeholder="Code (K4M-2P7)"
            className="w-[150px] rounded-lg border border-slate-300 px-3 py-2 font-mono text-sm uppercase"
          />
          {/* Stage first, because it is the widest cut: a Year 8 worksheet and
              a GCSE mock share no questions at all, and until this existed a
              tutor building one was offered the whole corpus. */}
          <select
            value={stage}
            onChange={(e) => reset(() => setStage(e.target.value))}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
          >
            <option value="">Any stage</option>
            <option value="KS3">KS3 &middot; Years 7-9</option>
            <option value="GCSE">GCSE &middot; Years 10-11</option>
          </select>
          <select
            value={source}
            onChange={(e) => reset(() => setSource(e.target.value))}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
          >
            <option value="">Any source</option>
            <option value="edexcel">Edexcel</option>
            <option value="aqa">AQA</option>
            <option value="practice-booklets">Practice booklets</option>
          </select>
          <select
            value={topic}
            onChange={(e) => reset(() => setTopic(e.target.value))}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
          >
            <option value="">All topics</option>
            {topics.map((t) => (
              <option key={t.id} value={t.id}>{t.name}{t.question_count ? ` (${t.question_count})` : ""}</option>
            ))}
          </select>
          <select
            value={calculator}
            onChange={(e) => reset(() => setCalculator(e.target.value))}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
          >
            <option value="">Calculator: any</option>
            <option value="no">Non-calculator</option>
            <option value="yes">Calculator</option>
          </select>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-slate-500">Band:</span>
          {DIFFICULTIES.map((d) => (
            <button
              key={d.value}
              type="button"
              onClick={() => reset(() => setDifficulty(difficulty === d.value ? "" : d.value))}
              className={`rounded-lg border px-3 py-1.5 text-sm ${
                difficulty === d.value ? "border-slate-800 bg-slate-800 text-white" : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
              }`}
            >
              {d.label}
            </button>
          ))}
          {(search || code || topic || difficulty || calculator || stage || source) && (
            <button
              type="button"
              onClick={() => reset(() => {
                setSearch(""); setCode(""); setTopic(""); setDifficulty("");
                setCalculator(""); setStage(""); setSource("");
              })}
              className="ml-1 text-sm text-slate-500 underline hover:text-slate-800"
            >
              Clear all
            </button>
          )}
          <span className="ml-auto text-sm text-slate-500">
            {loading ? "…" : `${total.toLocaleString("en-GB")} question${total === 1 ? "" : "s"}`}
          </span>
        </div>
      </div>

      {/* Results */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        {loading && rows.length === 0 && (
          <div className="flex items-center justify-center gap-2 py-16 text-slate-500">
            <Loader2 size={16} className="animate-spin" /> Loading…
          </div>
        )}
        {!loading && rows.length === 0 && (
          <div className="py-16 text-center text-sm text-slate-500">
            Nothing matches those filters.
          </div>
        )}

        {rows.map((r) => (
          <div key={r.code} className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-slate-100 px-4 py-3 last:border-b-0 hover:bg-slate-50/70">
            <span className="w-[86px] shrink-0 font-mono text-sm font-semibold tracking-wide text-slate-800">
              {r.code}
            </span>

            <div className="min-w-[180px] flex-1">
              <div className="truncate text-sm font-medium text-slate-900">
                {r.topic || <span className="text-slate-400">No topic</span>}
              </div>
              <div className="mt-0.5 flex items-center gap-2 text-xs text-slate-500">
                <FileText size={11} className="text-slate-400" />
                {r.paper_code} · {r.series} {r.year} · Q{r.number}
                {r.contains_diagram && (
                  <span className="inline-flex items-center gap-1 text-slate-400">
                    <ImageIcon size={11} /> diagram
                  </span>
                )}
              </div>
            </div>

            <Band value={r.difficulty} />

            {r.calculator_allowed === false && (
              <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs text-slate-600">
                <Calculator size={11} /> non-calc
              </span>
            )}

            <span className="w-[62px] shrink-0 text-right text-sm tabular-nums text-slate-600">
              {r.marks} mk{r.marks === 1 ? "" : "s"}
            </span>

            <span
              title={r.answers_unlocked ? "Answers released to students" : "Answers locked"}
              className={`inline-flex items-center gap-1 text-xs ${r.answers_unlocked ? "text-green-700" : "text-slate-400"}`}
            >
              {r.answers_unlocked ? <Unlock size={12} /> : <Lock size={12} />}
            </span>

            <a
              href={siteUrl ? `${siteUrl}/q/${r.code}` : "#"}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-white"
            >
              Preview <ExternalLink size={11} />
            </a>
          </div>
        ))}
      </div>

      {total > PAGE && (
        <div className="mt-4 flex items-center justify-between text-sm">
          <button
            type="button"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE))}
            className="rounded-lg border border-slate-300 px-3 py-1.5 disabled:opacity-40"
          >
            ← Previous
          </button>
          <span className="tabular-nums text-slate-500">
            {offset + 1}–{Math.min(offset + PAGE, total)} of {total.toLocaleString("en-GB")}
          </span>
          <button
            type="button"
            disabled={offset + PAGE >= total}
            onClick={() => setOffset(offset + PAGE)}
            className="rounded-lg border border-slate-300 px-3 py-1.5 disabled:opacity-40"
          >
            Next →
          </button>
        </div>
      )}

      <p className="mt-6 text-xs text-slate-400">
        These questions live in the question bank, not in this site. They are edited on the office PC
        and published from there. Students see only the code — never the paper, the series or the year.
      </p>
    </AdminShell>
  );
}
